import { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { FileCheck2, Download, RefreshCw, Landmark } from 'lucide-react';
import { admin, getErrorMessage } from '../../utils/api';

const _money = (n) => `$${Number(n || 0).toFixed(2)}`;

function _download(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}

export default function AdminST1Automation({ user, onLogout }) {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await admin.st1Filings();
      setFilings((res.data.filings || []).filter((f) => f.source === 'reconciliation_monthly'));
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to load filings')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doPreview = async () => {
    try {
      setBusy(true);
      const res = await admin.st1Preview(Number(year), Number(month));
      setPreview(res.data.report);
    } catch (e) { toast.error(getErrorMessage(e, 'Preview failed')); }
    finally { setBusy(false); }
  };

  const doGenerate = async () => {
    try {
      setBusy(true);
      const res = await admin.st1Generate({ year: Number(year), month: Number(month), mode: 'TEST' });
      toast.success(`Staged ${res.data.filing.filename}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Generate failed')); }
    finally { setBusy(false); }
  };

  const doDownload = async (f) => {
    try {
      const res = await admin.st1Download(f.period, f.mode);
      _download(res.data, `${f.filename}.zip`);
    } catch (e) { toast.error(getErrorMessage(e, 'Download failed')); }
  };

  const doIFileToggle = async (f) => {
    try {
      await admin.st1MarkIFileUploaded(f.period, f.mode, !f.uploaded_to_ifile);
      toast.success(!f.uploaded_to_ifile ? 'Marked uploaded to iFile' : 'Cleared iFile upload flag');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to update')); }
  };

  const doConfirm = async (f, status) => {
    try {
      const va_reference = status === 'accepted'
        ? (window.prompt('VA confirmation reference number (optional):') || '')
        : '';
      const message = status === 'rejected'
        ? (window.prompt('Rejection reason (optional):') || '')
        : '';
      await admin.st1RecordConfirmation(f.period, f.mode, { status, va_reference, message });
      toast.success(`Filing marked ${status}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to record confirmation')); }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-st1-automation-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="w-6 h-6 text-teal-700" /> ST-1 Automation
          </h1>
          <div className="text-gray-600 text-sm mt-1">
            Auto-build the Virginia ST-1 / ST-1A filing from the monthly reconciliation logs.
            <Badge className="ml-2 bg-teal-100 text-teal-800" data-testid="st1-submission-method">Submission: iFile (manual upload)</Badge>
            <Badge variant="outline" className="ml-2">ST1_MODE: TEST</Badge>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generate a Filing</CardTitle>
            <CardDescription>Aggregates every active DRLP's monthly reconciliation for the period.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 items-end">
              <div><Label className="text-xs">Year</Label><Input value={year} onChange={(e) => setYear(e.target.value)} className="w-28" data-testid="st1-year-input" /></div>
              <div><Label className="text-xs">Month</Label><Input value={month} onChange={(e) => setMonth(e.target.value)} className="w-24" data-testid="st1-month-input" /></div>
              <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="st1-preview-btn">Preview</Button>
              <Button className="bg-teal-700 hover:bg-teal-800" onClick={doGenerate} disabled={busy} data-testid="st1-generate-btn">
                <FileCheck2 className="w-4 h-4 mr-2" />Generate &amp; Stage
              </Button>
            </div>

            {preview && (
              <div className="mt-5 grid md:grid-cols-2 gap-4" data-testid="st1-preview">
                <div className="rounded-lg border p-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Period Totals — {preview.period?.label}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Gross Sales</span><b>{_money(preview.totals?.gross_sales)}</b></div>
                    <div className="flex justify-between"><span>Taxable Sales</span><b>{_money(preview.totals?.taxable_sales)}</b></div>
                    <div className="flex justify-between"><span>Tax Collected</span><b>{_money(preview.totals?.tax_collected)}</b></div>
                    <div className="flex justify-between"><span>Refund Tax</span><b>-{_money(preview.totals?.refund_tax_total)}</b></div>
                    <div className="flex justify-between"><span>Transactions</span><b>{preview.totals?.order_count}</b></div>
                    <div className="flex justify-between"><span>Retailers</span><b>{preview.by_retailer?.length || 0}</b></div>
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Category Breakdown</p>
                  <Table><TableBody>
                    {(preview.category_breakdown || []).map((c) => (
                      <TableRow key={c.category_name}><TableCell>{c.category_name}</TableCell><TableCell className="text-right">{_money(c.sales_in_category)}</TableCell><TableCell className="text-right">{_money(c.tax_collected_in_category)}</TableCell></TableRow>
                    ))}
                  </TableBody></Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Staged Filings</CardTitle>
            <Button variant="outline" onClick={load} data-testid="st1-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
            ) : filings.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="st1-empty">No automated filings staged yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Tax Collected</TableHead>
                    <TableHead>DRLPs</TableHead>
                    <TableHead>iFile</TableHead>
                    <TableHead>VA Confirmation</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filings.map((f) => (
                    <TableRow key={`${f.period}-${f.mode}`} data-testid={`st1-row-${f.period}-${f.mode}`}>
                      <TableCell className="font-mono text-xs" title={f.file_sha256 ? `sha256:${f.file_sha256}` : ''}>{f.filename}</TableCell>
                      <TableCell>{f.period_label}</TableCell>
                      <TableCell className="text-xs text-gray-500">{f.due_date || '—'}</TableCell>
                      <TableCell className="text-right">{_money(f.report_totals?.tax_collected)}</TableCell>
                      <TableCell>{f.drlp_count}</TableCell>
                      <TableCell>
                        <Button size="sm" variant={f.uploaded_to_ifile ? 'default' : 'outline'}
                          className={f.uploaded_to_ifile ? 'bg-emerald-600 hover:bg-emerald-700 h-7' : 'h-7'}
                          onClick={() => doIFileToggle(f)} data-testid={`st1-ifile-${f.period}-${f.mode}`}>
                          {f.uploaded_to_ifile ? 'Yes' : 'No'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {f.confirmation?.status === 'accepted' && <Badge className="bg-emerald-100 text-emerald-800">Accepted{f.confirmation.va_reference ? ` · ${f.confirmation.va_reference}` : ''}</Badge>}
                        {f.confirmation?.status === 'rejected' && <Badge className="bg-red-100 text-red-800">Rejected</Badge>}
                        {!f.confirmation?.status && <span className="text-xs text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => doDownload(f)} data-testid={`st1-download-${f.period}-${f.mode}`}><Download className="w-3 h-3 mr-1" />ZIP</Button>
                        <Button size="sm" variant="outline" className="text-emerald-700" disabled={f.locked} onClick={() => doConfirm(f, 'accepted')} data-testid={`st1-accept-${f.period}-${f.mode}`}>Accept</Button>
                        <Button size="sm" variant="outline" className="text-red-600" disabled={f.locked} onClick={() => doConfirm(f, 'rejected')} data-testid={`st1-reject-${f.period}-${f.mode}`}>Reject</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
